import {
  createRedoPartnersRpcClient,
  type RedoPartnersRpcClient,
} from "@redotech/redo-partners-api/client";
import {
  type Context,
  createContext,
  type ReactNode,
  useContext,
  useMemo,
} from "react";

import { REDO_PARTNERS_SERVER_URL } from "../config";

export const RedoPartnersRpcClientContext: Context<
  RedoPartnersRpcClient | undefined
> = createContext<RedoPartnersRpcClient | undefined>(undefined);
RedoPartnersRpcClientContext.displayName = "RedoPartnersRpcClientContext";

export function RedoPartnersRpcClientProvider({
  children,
  authToken,
}: {
  children: ReactNode | ReactNode[];
  authToken: string | undefined;
}) {
  // `ClientOptions.headers` is captured at construction time, so the
  // client must be reconstructed whenever the token changes. useMemo
  // keyed on the token gives us the same identity across renders that
  // share an auth state.
  const client = useMemo(() => {
    return createRedoPartnersRpcClient({
      baseURL: new URL(`${REDO_PARTNERS_SERVER_URL}/rpc`),
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    });
  }, [authToken]);
  return (
    <RedoPartnersRpcClientContext.Provider value={client}>
      {children}
    </RedoPartnersRpcClientContext.Provider>
  );
}

export function useRedoPartnersRpcClient(): RedoPartnersRpcClient {
  const client = useContext(RedoPartnersRpcClientContext);
  if (!client) {
    throw new Error(
      "useRedoPartnersRpcClient must be used within RedoPartnersRpcClientProvider",
    );
  }
  return client;
}
